import React, { useContext, useEffect, useState } from 'react';
import {
	IOfferContext,
	ITypeVignetteOrderingProcessFields,
	OfferContext,
	VignetteContext,
	countryUrlMap,
	fetchInitialVignetteData,
	getSupplierIdByCountry,
	setRouteLangParam,
	Locales,
	resetStorageAndContext,
	getVignetteOrderingProcessCollection,
	getUrlBasePath,
} from '@adac-mp/shared';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
	Check,
	ContentLoader,
	Toast,
	getCorrespondingHelpDrawerContent,
	getCountryName,
	getDrawerHelperData,
	withLocalization,
} from '@adac-mp/components';
import { trackFunnelStart, trackFunnelView } from '@adac-mp/components/src/utils/tracking/tracking';
import { CategorySelection } from '../../components/vignette/categorySelection';
import Configuration from '../../components/configuration';
import { TollRoutesSelection } from '../../components/vignette/tollRoutesSelection';
import { ProductSelection } from '../../components/vignette/selection';
import { TimeSelection } from '../../components/vignette/timeSelection';
import { PlatesSelection } from '../../components/vignette/platesSelection';
import { CountrySelection } from '../../components/one-cart/countrySelection';

const OneCartConfigurationComponent = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const supplierId = getSupplierIdByCountry(location);
	const [reuse, setReuse] = useState<boolean>(false);
	const {
		productsTypeMap,
		selectedProducts,
		cartItems,
		selectedCountries,
		contentfulLabels,
		updateIsTransitionPeriod,
		updateContentfulLabels,
		deleteSelectedProducts,
		updateCart,
	} = useContext(VignetteContext);
	const { lang } = useParams();
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const { updateVignetteOffers, updateAllCountries, updateAllOffers, updateTollSectionOffers } =
		useContext<IOfferContext>(OfferContext);
	const { error, data, loading } = useQuery(getVignetteOrderingProcessCollection, {
		variables: { lang: lang || 'de', slug: 'one-cart' },
	});
	const [countryRemoved, setCountryRemoved] = useState(false);
	const isSchweizSelected = selectedCountries.find((selectedCountry) => selectedCountry.supplierId === 14);
	const isOnlySchweizSelected = isSchweizSelected && selectedCountries.length === 1;

	useEffect(() => {
		resetStorageAndContext(
			supplierId,
			deleteSelectedProducts,
			updateCart,
			updateTollSectionOffers,
			updateIsTransitionPeriod,
		);

		fetchInitialVignetteData(supplierId, {
			navigate,
			lang: lang as Locales,
			updateVignetteOffers,
			updateAllOffers,
			updateAllCountries,
			updateTollSectionOffers,
			updateIsTransitionPeriod,
		});
	}, [lang]);

	useEffect(() => {
		setReuse(cartItems.length > 0);
		if (cartItems.length > 0) {
			trackFunnelView({
				label:
					currentLabels?.tollCategoryReuseHeadline ||
					'Für welches Fahrzeug benötigen Sie das weitere Produkt?',
				element: 'Reuse Fahrzeugauswahl',
				trackOnce: true,
			});
		} else {
			trackFunnelStart({
				label: currentLabels?.tollCategoriesHeadline || 'Mit welchem Fahrzeug sind Sie unterwegs?',
				element: 'Fahrzeugauswahl',
				trackOnce: true,
			});
		}
	}, [cartItems]);

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignetteOrderingProcessCollection.items[0]);
		}
	}, [data]);

	const onFinished = () => {
		navigate(getUrlBasePath(`/${countryUrlMap[supplierId]}/product-done${setRouteLangParam(lang)}`));
	};

	const initialSteps = [
		{
			title: `${reuse ? 'Reuse ' : ''}Fahrzeugauswahl`,
			component: <CategorySelection key="category-selection" />,
		},
		{
			title: 'Länderauswahl',
			component: (
				<CountrySelection
					key="country-selection"
					tracking={{
						funnel_view: {
							label: currentLabels?.oneCartHeadline || 'Durch welche Länder reisen Sie?',
							element: 'Länderauswahl',
							trackOnce: true,
						},
					}}
				/>
			),
		},
	];

	const [componentList, setComponentList] = useState(initialSteps);

	useEffect(() => {
		setComponentList(initialSteps);
	}, [currentLabels?.oneCartCountries?.items, reuse]);

	useEffect(() => {
		const hasVignette =
			productsTypeMap.get('autVignette') || productsTypeMap.get('sloVignette') || productsTypeMap.get('schweiz');
		const hasSectionToll = productsTypeMap.get('sectionToll');
		const hasFlexProduct = productsTypeMap.get('flexProduct');
		const supplierSortOrder = [3, 14, 7];

		const productSelection = selectedCountries
			.sort((a, b) =>
				supplierSortOrder.indexOf(a.supplierId) > supplierSortOrder.indexOf(b.supplierId) ? 1 : -1,
			)
			.map(({ name, supplierId: currSuppId }) => ({
				title: `Produktauswahl ${getCountryName(currSuppId, currentLabels?.oneCartCountries)}`,
				component: (
					<ProductSelection
						key={`product-selection-${name}`}
						supplierId={currSuppId}
						helpContent={getCorrespondingHelpDrawerContent(currSuppId, currentLabels?.helpDrawers)}
						tracking={{
							funnel_view: {
								label:
									currentLabels?.headlineProductSelection?.replace(
										'{country}',
										getCountryName(currSuppId, currentLabels?.oneCartCountries),
									) || 'Was benötigen Sie?',
								element: `Produktauswahl ${getCountryName(
									currSuppId,
									currentLabels?.oneCartCountries,
								)}`,
								trackOnce: true,
							},
						}}
					/>
				),
			}));

		setComponentList((prev) => {
			const currComponentList = prev.slice(0, 2).concat(productSelection);
			if (hasSectionToll && !hasFlexProduct) {
				currComponentList.splice(3, 0, {
					title: 'Streckenauswahl Österreich',
					component: (
						<TollRoutesSelection
							key="section-toll-selection"
							helpContent={getDrawerHelperData('tollSection', currentLabels?.helpDrawers)}
							tracking={{
								funnel_view: {
									label:
										currentLabels?.headlineTollSection || 'Wählen Sie eine oder mehrere Strecken',
									element: `Streckenauswahl Österreich`,
									trackOnce: true,
								},
							}}
						/>
					),
				});
			}
			if (hasVignette) {
				currComponentList.push({
					title: 'Gültigkeit',
					component: (
						<TimeSelection
							key="validity-selection"
							inputDisabled={isOnlySchweizSelected}
							handleCountryRemoved={setCountryRemoved}
							tracking={{
								funnel_view: {
									label: currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?',
									element: 'Gültigkeit',
									trackOnce: true,
								},
							}}
						/>
					),
				});
			}
			if (!reuse) {
				currComponentList.push({
					title: 'Kennzeichen',
					component: (
						<PlatesSelection
							key="licenseplate"
							hasSpecialPlates
							helpContent={
								isSchweizSelected &&
								getDrawerHelperData('plateSelectionSchweiz', currentLabels?.helpDrawers)
							}
							tracking={{
								funnel_view: {
									label: currentLabels?.headlineLicenseplate || 'Wie lautet Ihr Kennzeichen?',
									element: 'Kennzeichen',
									trackOnce: true,
								},
							}}
						/>
					),
				});
			}
			return currComponentList;
		});
	}, [selectedCountries, selectedProducts, productsTypeMap, reuse]);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Configuration
						withStepReset
						supplierHasMultipleProducts
						reuse={reuse}
						componentList={componentList}
						supplierId={supplierId}
						setReuse={setReuse}
						onFinished={onFinished}
					/>
					{countryRemoved && (
						<Toast
							className={`transition-transform duration-300 translate-x-0`}
							position="bottom"
							content={`Die slowenische Vignette wurde abgewählt.`}
							icon={<Check size="16" />}
							filledIconColor="primary"
							onToastFinished={() => {
								setCountryRemoved(false);
							}}
						/>
					)}
				</>
			)}
		</>
	);
};

export const OneCartConfiguration = withLocalization(OneCartConfigurationComponent);
